/**
 * ShotFilterChips Component
 *
 * Shot filter chips for detail modals (action detail and rich shot detail).
 * Uses multi-line chip pattern displaying % | TS% in the bottom row.
 *
 * Accepts a shot pool and manages shot-level cross-filter counts.
 */

import { useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Shot, ShotFilters, getShotRegionCategory } from "./types";
import {
  toggleFilter,
  applyFiltersExcept,
  formatContestLevel,
  formatSnakeCase,
  formatPct,
  getHeatColor,
} from "./utils";
import "./ShotDetailModal.scss";

// ============================================================================
// Types
// ============================================================================

interface ShotFilterChipsProps {
  /** Shots to filter (the shot pool for these filters) */
  shots: Shot[];
  /** Current shot filter state */
  filters: ShotFilters;
  /** Callback when shot filters change */
  onChange: (filters: ShotFilters) => void;
  /** Whether filters are disabled */
  disabled?: boolean;
  /** Ordered array of group keys for hierarchical breakdown */
  expandedGroups?: string[];
  /** Callback when a filter header is clicked for breakdown */
  onExpandGroup?: (groupKey: string, isModifierClick: boolean) => void;
}

interface ShotFilterOption<T> {
  value: T;
  label: string;
  count: number;
  tsPct: number | null;
}

interface ShotPlayerOption {
  name: string;
  count: number;
  tsPct: number | null;
}

// ============================================================================
// Helpers
// ============================================================================

/** Compute TS% for an array of shots: ptsScored / count / 2 */
function computeTS(shots: Shot[]): number | null {
  if (shots.length === 0) return null;
  const totalPts = shots.reduce((sum, s) => sum + s.ptsScored, 0);
  return totalPts / shots.length / 2;
}

/**
 * Single-pass helper that groups cross-filtered shots by a key function
 * and computes both count and TS% per group.
 */
function computeShotOptionStats<T extends string>(
  crossFiltered: Shot[],
  getKey: (s: Shot) => T | null,
): Map<T, { count: number; tsPct: number | null }> {
  const groups = new Map<T, Shot[]>();
  for (const s of crossFiltered) {
    const key = getKey(s);
    if (key === null) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s);
  }
  const result = new Map<T, { count: number; tsPct: number | null }>();
  groups.forEach((shots, key) => {
    result.set(key, { count: shots.length, tsPct: computeTS(shots) });
  });
  return result;
}

// ============================================================================
// Main Component
// ============================================================================

export default function ShotFilterChips({
  shots,
  filters,
  onChange,
  disabled = false,
  expandedGroups = [],
  onExpandGroup,
}: ShotFilterChipsProps) {
  // Label click handler for breakdown expansion
  const handleLabelClick = (groupKey: string, event: React.MouseEvent) => {
    onExpandGroup?.(groupKey, event.metaKey || event.ctrlKey);
  };

  // Get hierarchy level for a group key
  const getHierarchyLevel = (groupKey: string): number | undefined => {
    const idx = expandedGroups.indexOf(groupKey);
    return idx >= 0 ? idx + 1 : undefined;
  };

  // Whether a group can be nested ("+") — only when breakdown is active, not at max depth,
  // and this group isn't already in the hierarchy
  const canNest = (groupKey: string): boolean =>
    !!onExpandGroup &&
    expandedGroups.length > 0 &&
    expandedGroups.length < 3 &&
    !expandedGroups.includes(groupKey);

  const handleNest = (groupKey: string) => {
    onExpandGroup?.(groupKey, true); // true = modifier click = nest
  };
  // Category visibility — hide when ≤1 unique non-null value in the shot pool
  const visibility = useMemo(() => {
    const unique = (vals: (string | boolean | null)[]) =>
      new Set(vals.filter((v) => v !== null)).size;

    return {
      assisted: new Set(shots.map((s) => s.assisted)).size > 1,
      fouled: new Set(shots.map((s) => s.fouled)).size > 1,
      shotType: unique(shots.map((s) => s.shotType)) > 1,
      complexShotType: unique(shots.map((s) => s.complexShotType)) > 1,
      contestLevel: unique(shots.map((s) => s.contestLevel)) > 1,
      catchAndShoot: unique(shots.map((s) => s.catchAndShoot)) > 1,
      regionCategory:
        unique(shots.map((s) => getShotRegionCategory(s.region))) > 1,
      shooter: unique(shots.map((s) => s.shooter)) > 1,
      passer: unique(shots.map((s) => s.passer)) > 1,
      // Absent before R3 and on sources without one: the dropdown auto-hides.
      contester: unique(shots.map((s) => s.contester ?? null)) > 1,
    };
  }, [shots]);

  // Cross-filter counts + TS% for each category
  const options = useMemo(() => {
    const forAssisted = applyFiltersExcept(shots, filters, "assisted");
    const forFouled = applyFiltersExcept(shots, filters, "fouled");
    const forShotType = applyFiltersExcept(shots, filters, "shotType");
    const forComplexShotType = applyFiltersExcept(
      shots,
      filters,
      "complexShotType",
    );
    const forContest = applyFiltersExcept(shots, filters, "contestLevel");
    const forCAS = applyFiltersExcept(shots, filters, "catchAndShoot");
    const forRegion = applyFiltersExcept(shots, filters, "regionCategory");
    const forShooter = applyFiltersExcept(shots, filters, "shooter");
    const forPasser = applyFiltersExcept(shots, filters, "passer");
    const forContester = applyFiltersExcept(shots, filters, "contester");

    // Assisted
    const assistedStats = computeShotOptionStats(
      forAssisted,
      (s): "assisted" | "unassisted" =>
        s.assisted ? "assisted" : "unassisted",
    );
    const assistedOpts: ShotFilterOption<"assisted" | "unassisted">[] = [
      {
        value: "assisted",
        label: "Assisted",
        count: assistedStats.get("assisted")?.count ?? 0,
        tsPct: assistedStats.get("assisted")?.tsPct ?? null,
      },
      {
        value: "unassisted",
        label: "Unassisted",
        count: assistedStats.get("unassisted")?.count ?? 0,
        tsPct: assistedStats.get("unassisted")?.tsPct ?? null,
      },
    ];

    // Fouled
    const fouledStats = computeShotOptionStats(
      forFouled,
      (s): "fouled" | "not_fouled" => (s.fouled ? "fouled" : "not_fouled"),
    );
    const fouledOpts: ShotFilterOption<"fouled" | "not_fouled">[] = [
      {
        value: "fouled",
        label: "Fouled",
        count: fouledStats.get("fouled")?.count ?? 0,
        tsPct: fouledStats.get("fouled")?.tsPct ?? null,
      },
      {
        value: "not_fouled",
        label: "Not Fouled",
        count: fouledStats.get("not_fouled")?.count ?? 0,
        tsPct: fouledStats.get("not_fouled")?.tsPct ?? null,
      },
    ];

    // Shot Type — data-driven from actual values
    const shotTypeStats = computeShotOptionStats(
      forShotType,
      (s) => s.shotType,
    );
    const shotTypeOpts: ShotFilterOption<string>[] = Array.from(
      shotTypeStats.entries(),
    )
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: formatSnakeCase(value),
        count: s.count,
        tsPct: s.tsPct,
      }));

    // Complex Shot Type — dynamic values from data, sorted by count desc
    const complexShotTypeStats = computeShotOptionStats(
      forComplexShotType,
      (s) => s.complexShotType,
    );
    const complexShotTypeOpts: ShotFilterOption<string>[] = Array.from(
      complexShotTypeStats.entries(),
    )
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: value
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
        count: s.count,
        tsPct: s.tsPct,
      }));

    // Contest Level — data-driven from actual values
    const contestStats = computeShotOptionStats(
      forContest,
      (s) => s.contestLevel,
    );
    const contestOpts: ShotFilterOption<string>[] = Array.from(
      contestStats.entries(),
    )
      .sort((a, b) => b[1].count - a[1].count)
      .map(([value, s]) => ({
        value,
        label: formatContestLevel(value),
        count: s.count,
        tsPct: s.tsPct,
      }));

    // Catch & Shoot
    const casStats = computeShotOptionStats(forCAS, (s): "yes" | "no" | null =>
      s.catchAndShoot === null ? null : s.catchAndShoot ? "yes" : "no",
    );
    const casOpts: ShotFilterOption<"yes" | "no">[] = [
      {
        value: "yes",
        label: "C&S",
        count: casStats.get("yes")?.count ?? 0,
        tsPct: casStats.get("yes")?.tsPct ?? null,
      },
      {
        value: "no",
        label: "Off Dribble",
        count: casStats.get("no")?.count ?? 0,
        tsPct: casStats.get("no")?.tsPct ?? null,
      },
    ];

    // Region Category — data-driven from actual values
    const regionStats = computeShotOptionStats(forRegion, (s) =>
      getShotRegionCategory(s.region),
    );
    const REGION_LABELS: Record<string, string> = {
      rim: "Rim",
      shortMid: "Short Mid",
      long2: "Long 2",
      corner3: "Corner 3",
      atb: "ATB 3",
    };
    // Preferred display order for known region categories
    const REGION_ORDER = ["rim", "shortMid", "long2", "corner3", "atb"];
    const regionOpts: ShotFilterOption<string>[] = Array.from(
      regionStats.entries(),
    )
      .sort((a, b) => {
        const ai = REGION_ORDER.indexOf(a[0]);
        const bi = REGION_ORDER.indexOf(b[0]);
        // Known regions sort by preferred order, unknown sort by count desc after known
        if (ai >= 0 && bi >= 0) return ai - bi;
        if (ai >= 0) return -1;
        if (bi >= 0) return 1;
        return b[1].count - a[1].count;
      })
      .map(([value, s]) => ({
        value,
        label: REGION_LABELS[value] || formatSnakeCase(value),
        count: s.count,
        tsPct: s.tsPct,
      }));

    // Shooter / Passer — group by name, compute count + TS%
    const shooterStats = computeShotOptionStats(
      forShooter,
      (s) => s.shooter || null,
    );
    const shooterOpts: ShotPlayerOption[] = Array.from(shooterStats.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .map(([name, s]) => ({ name, count: s.count, tsPct: s.tsPct }));

    const passerStats = computeShotOptionStats(forPasser, (s) => s.passer);
    const passerOpts: ShotPlayerOption[] = Array.from(passerStats.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .map(([name, s]) => ({ name, count: s.count, tsPct: s.tsPct }));

    const contesterStats = computeShotOptionStats(
      forContester,
      (s) => s.contester ?? null,
    );
    const contesterOpts: ShotPlayerOption[] = Array.from(
      contesterStats.entries(),
    )
      .sort((a, b) => b[1].count - a[1].count)
      .map(([name, s]) => ({ name, count: s.count, tsPct: s.tsPct }));

    return {
      assisted: assistedOpts,
      fouled: fouledOpts,
      shotType: shotTypeOpts,
      complexShotType: complexShotTypeOpts,
      contestLevel: contestOpts,
      catchAndShoot: casOpts,
      regionCategory: regionOpts,
      shooter: shooterOpts,
      passer: passerOpts,
      contester: contesterOpts,
    };
  }, [shots, filters]);

  // Assisted filter interaction with passer
  const handleAssistedToggle = (value: "assisted" | "unassisted") => {
    if (disabled) return;
    const newFilters = toggleFilter(filters, "assisted", value);
    // If only "unassisted" selected, clear passer filter
    if (
      newFilters.assisted.has("unassisted") &&
      !newFilters.assisted.has("assisted")
    ) {
      onChange({ ...newFilters, passer: new Set<string>() });
    } else {
      onChange(newFilters);
    }
  };

  const isPasserDisabled =
    disabled ||
    (filters.assisted.has("unassisted") && !filters.assisted.has("assisted"));

  // Helper to generate common props for standard shot filter chip groups.
  // Eliminates repeated onToggle/onSetReplace/onLabelClick/hierarchy/nest boilerplate.
  const standardProps = <K extends keyof ShotFilters>(key: K) => ({
    onToggle: ((v: any) => onChange(toggleFilter(filters, key, v))) as any,
    onSetReplace: ((s: any) => onChange({ ...filters, [key]: s })) as any,
    onLabelClick: (e: React.MouseEvent) => handleLabelClick(key, e),
    hierarchyLevel: getHierarchyLevel(key),
    onNestClick: canNest(key) ? () => handleNest(key) : undefined,
  });

  // Helper for data-driven "empty = all" chip groups (shotType, contestLevel, regionCategory).
  // Creates the toggle/replace callbacks that treat empty set as "all selected".
  const emptyMeansAllProps = (
    key: "shotType" | "contestLevel" | "regionCategory",
    optionValues: string[],
  ) => ({
    emptyMeansAll: true,
    onToggle: (v: string) => {
      if (disabled) return;
      const currentSet = filters[key];
      if (currentSet.size === 0) {
        onChange({
          ...filters,
          [key]: new Set(optionValues.filter((val) => val !== v)),
        });
      } else if (currentSet.has(v)) {
        if (currentSet.size > 1) {
          const newSet = new Set(currentSet);
          newSet.delete(v);
          onChange({ ...filters, [key]: newSet });
        }
      } else {
        const newSet = new Set(currentSet);
        newSet.add(v);
        onChange({
          ...filters,
          [key]:
            newSet.size >= optionValues.length ? new Set<string>() : newSet,
        });
      }
    },
    onSetReplace: (s: Set<string>) => onChange({ ...filters, [key]: s }),
    onLabelClick: (e: React.MouseEvent) => handleLabelClick(key, e),
    hierarchyLevel: getHierarchyLevel(key),
    onNestClick: canNest(key) ? () => handleNest(key) : undefined,
  });

  return (
    <Stack spacing={1.5}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        Shot Filters
      </Typography>

      {/* Assisted */}
      {visibility.assisted && (
        <ShotChipGroup
          label="Assisted"
          options={options.assisted}
          selectedSet={filters.assisted}
          onToggle={handleAssistedToggle}
          onSetReplace={(s) => {
            const newFilters = { ...filters, assisted: s };
            // If only "unassisted" selected, clear passer filter
            if (s.has("unassisted") && !s.has("assisted")) {
              onChange({ ...newFilters, passer: new Set<string>() });
            } else {
              onChange(newFilters);
            }
          }}
          disabled={disabled || filters.passer.size > 0}
          onLabelClick={(e) => handleLabelClick("assisted", e)}
          hierarchyLevel={getHierarchyLevel("assisted")}
          onNestClick={
            canNest("assisted") ? () => handleNest("assisted") : undefined
          }
        />
      )}

      {/* Fouled */}
      {visibility.fouled && (
        <ShotChipGroup
          label="Fouled"
          options={options.fouled}
          selectedSet={filters.fouled}
          disabled={disabled}
          {...standardProps("fouled")}
        />
      )}

      {/* Shot Type — data-driven */}
      {visibility.shotType && (
        <ShotChipGroup
          label="Shot Type"
          options={options.shotType}
          selectedSet={filters.shotType}
          disabled={disabled}
          {...emptyMeansAllProps(
            "shotType",
            options.shotType.map((o) => o.value),
          )}
        />
      )}

      {/* Complex Shot Type */}
      {visibility.complexShotType && (
        <ShotChipGroup
          label="Complex Shot Type"
          options={options.complexShotType}
          selectedSet={filters.complexShotType}
          disabled={disabled}
          emptyMeansAll
          onToggle={(v) => {
            if (disabled) return;
            const allValues = options.complexShotType.map((o) => o.value);
            const currentSet = filters.complexShotType;
            if (currentSet.size === 0) {
              onChange({
                ...filters,
                complexShotType: new Set(allValues.filter((val) => val !== v)),
              });
            } else if (currentSet.has(v)) {
              if (currentSet.size > 1) {
                const newSet = new Set(currentSet);
                newSet.delete(v);
                onChange({ ...filters, complexShotType: newSet });
              }
            } else {
              const newSet = new Set(currentSet);
              newSet.add(v);
              onChange({
                ...filters,
                complexShotType:
                  newSet.size >= allValues.length ? new Set<string>() : newSet,
              });
            }
          }}
          onSetReplace={(s) => onChange({ ...filters, complexShotType: s })}
          onLabelClick={standardProps("complexShotType").onLabelClick}
          hierarchyLevel={standardProps("complexShotType").hierarchyLevel}
          onNestClick={standardProps("complexShotType").onNestClick}
        />
      )}

      {/* Contest Level — data-driven */}
      {visibility.contestLevel && (
        <ShotChipGroup
          label="Contest Level"
          options={options.contestLevel}
          selectedSet={filters.contestLevel}
          disabled={disabled}
          {...emptyMeansAllProps(
            "contestLevel",
            options.contestLevel.map((o) => o.value),
          )}
        />
      )}

      {/* Catch & Shoot */}
      {visibility.catchAndShoot && (
        <ShotChipGroup
          label="Creation"
          options={options.catchAndShoot}
          selectedSet={filters.catchAndShoot}
          disabled={disabled}
          {...standardProps("catchAndShoot")}
        />
      )}

      {/* Region — data-driven */}
      {visibility.regionCategory && (
        <ShotChipGroup
          label="Region"
          options={options.regionCategory}
          selectedSet={filters.regionCategory}
          disabled={disabled}
          {...emptyMeansAllProps(
            "regionCategory",
            options.regionCategory.map((o) => o.value),
          )}
        />
      )}

      {/* Shooter / Passer dropdowns — 2-column layout */}
      {(visibility.shooter || visibility.passer) && (
        <Stack direction="row" spacing={1.5}>
          {visibility.shooter && (
            <Box sx={{ flex: 1 }}>
              <ShotPlayerDropdown
                label="Shooter"
                options={options.shooter}
                selected={filters.shooter}
                onChange={(val) =>
                  onChange({ ...filters, shooter: new Set(val) })
                }
                disabled={disabled}
                onLabelClick={(e) => handleLabelClick("shooter", e)}
                hierarchyLevel={getHierarchyLevel("shooter")}
                onNestClick={
                  canNest("shooter") ? () => handleNest("shooter") : undefined
                }
              />
            </Box>
          )}
          {visibility.passer && (
            <Box sx={{ flex: 1 }}>
              <ShotPlayerDropdown
                label="Passer"
                options={options.passer}
                selected={filters.passer}
                onChange={(val) =>
                  onChange({ ...filters, passer: new Set(val) })
                }
                disabled={isPasserDisabled}
                onLabelClick={(e) => handleLabelClick("passer", e)}
                hierarchyLevel={getHierarchyLevel("passer")}
                onNestClick={
                  canNest("passer") ? () => handleNest("passer") : undefined
                }
              />
            </Box>
          )}
        </Stack>
      )}
      {visibility.contester && (
        <Stack direction="row" spacing={1.5}>
          <Box sx={{ flex: 1 }} data-testid="contester-filter">
            <ShotPlayerDropdown
              label="Contester"
              options={options.contester}
              selected={filters.contester ?? new Set<string>()}
              onChange={(val) =>
                onChange({ ...filters, contester: new Set(val) })
              }
              disabled={disabled}
              onLabelClick={(e) => handleLabelClick("contester", e)}
              hierarchyLevel={getHierarchyLevel("contester")}
              onNestClick={
                canNest("contester") ? () => handleNest("contester") : undefined
              }
            />
          </Box>
          {/* Keeps the dropdown the same width as Shooter / Passer above */}
          <Box sx={{ flex: 1 }} />
        </Stack>
      )}
    </Stack>
  );
}

// ============================================================================
// ShotChipGroup — Multi-line chips with % | TS%
// ============================================================================

interface ShotChipGroupProps<T> {
  label: string;
  options: ShotFilterOption<T>[];
  selectedSet: Set<T>;
  onToggle: (value: T, event?: React.MouseEvent) => void;
  disabled: boolean;
  onLabelClick?: (event: React.MouseEvent) => void;
  hierarchyLevel?: number;
  /** Show "+" icon to nest this group into the breakdown hierarchy */
  onNestClick?: () => void;
  /** When true, empty set means "all selected" — chips show selected when set is empty */
  emptyMeansAll?: boolean;
  /**
   * Callback for Cmd/Ctrl+click "isolate" and "select all" behavior.
   * Receives the new Set to apply. When provided, ShotChipGroup handles
   * modifier key detection internally.
   */
  onSetReplace?: (newSet: Set<T>) => void;
}

function ShotChipGroup<T>({
  label,
  options,
  selectedSet,
  onToggle,
  disabled,
  onLabelClick,
  hierarchyLevel,
  onNestClick,
  emptyMeansAll = false,
  onSetReplace,
}: ShotChipGroupProps<T>) {
  const total = options.reduce((sum, o) => sum + o.count, 0);
  const allValues = options.map((o) => o.value);

  // Determine if all options are currently selected
  const isAllSelected = emptyMeansAll
    ? selectedSet.size === 0
    : allValues.every((v) => selectedSet.has(v));

  const handleClick = (value: T, e: React.MouseEvent) => {
    if (disabled) return;
    const isModifier = e.metaKey || e.ctrlKey;

    if (isModifier && onSetReplace) {
      const isSelected = emptyMeansAll
        ? selectedSet.size === 0 || selectedSet.has(value)
        : selectedSet.has(value);

      if (isAllSelected) {
        // From "all" → isolate just this one
        onSetReplace(new Set([value]));
      } else if (isSelected) {
        // From partial, on a selected chip → reset to all
        if (emptyMeansAll) {
          onSetReplace(new Set<T>());
        } else {
          onSetReplace(new Set(allValues));
        }
      } else {
        // From partial, on an unselected chip → isolate just this one
        onSetReplace(new Set([value]));
      }
      return;
    }

    onToggle(value, e);
  };

  return (
    <Box className="filter-group">
      <Box className="filter-group__label-row">
        <Typography
          variant="caption"
          className="filter-group__label"
          onClick={onLabelClick}
          sx={{
            cursor: onLabelClick ? "pointer" : undefined,
            textDecoration: hierarchyLevel ? "underline" : undefined,
            color: hierarchyLevel ? "primary.main" : undefined,
            "&:hover": onLabelClick ? { color: "primary.main" } : undefined,
          }}
        >
          {label}
          {hierarchyLevel && (
            <span className="filter-group__level-badge">{hierarchyLevel}</span>
          )}
        </Typography>
        {onNestClick && (
          <IconButton
            size="small"
            onClick={onNestClick}
            className="filter-group__nest-btn"
            title="Add as nested breakdown"
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {options.map((option) => {
          const isSelected = emptyMeansAll
            ? selectedSet.size === 0 || selectedSet.has(option.value)
            : selectedSet.has(option.value);
          const tsColor = getHeatColor(option.tsPct, 0.48, 0.62);

          return (
            <Chip
              key={String(option.value)}
              label={
                <div className="filter-chip__content">
                  <div className="filter-chip__name">{option.label}</div>
                  <div className="filter-chip__stats">
                    <span className="filter-chip__stat">
                      {formatPct(option.count, total)}
                    </span>
                    <span
                      className="filter-chip__stat"
                      style={
                        tsColor
                          ? {
                              backgroundColor: tsColor.bg,
                              color: tsColor.text,
                              textShadow: tsColor.textShadow,
                            }
                          : undefined
                      }
                    >
                      {option.tsPct !== null
                        ? (option.tsPct * 100).toFixed(1) + "%"
                        : "\u2014"}
                    </span>
                  </div>
                </div>
              }
              size="small"
              variant="outlined"
              onClick={(e) => handleClick(option.value, e)}
              disabled={disabled || option.count === 0}
              className={`filter-chip ${
                isSelected ? "filter-chip--selected" : ""
              }`}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

// ============================================================================
// ShotPlayerDropdown — Multi-select with count + TS%
// ============================================================================

interface ShotPlayerDropdownProps {
  label: string;
  options: ShotPlayerOption[];
  selected: Set<string>;
  onChange: (values: string[]) => void;
  disabled: boolean;
  onLabelClick?: (event: React.MouseEvent) => void;
  hierarchyLevel?: number;
  onNestClick?: () => void;
}

function ShotPlayerDropdown({
  label,
  options,
  selected,
  onChange,
  disabled,
  onLabelClick,
  hierarchyLevel,
  onNestClick,
}: ShotPlayerDropdownProps) {
  return (
    <Box className="filter-group">
      <Box className="filter-group__label-row">
        <Typography
          variant="caption"
          className="filter-group__label"
          onClick={onLabelClick}
          sx={{
            cursor: onLabelClick ? "pointer" : undefined,
            textDecoration: hierarchyLevel ? "underline" : undefined,
            color: hierarchyLevel ? "primary.main" : undefined,
            "&:hover": onLabelClick ? { color: "primary.main" } : undefined,
          }}
        >
          {label}
          {hierarchyLevel && (
            <span className="filter-group__level-badge">{hierarchyLevel}</span>
          )}
        </Typography>
        {onNestClick && (
          <IconButton
            size="small"
            onClick={onNestClick}
            className="filter-group__nest-btn"
            title="Add as nested breakdown"
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <FormControl fullWidth size="small" disabled={disabled}>
        <Select
          multiple
          value={Array.from(selected)}
          onChange={(e) => onChange(e.target.value as string[])}
          input={<OutlinedInput />}
          renderValue={(sel) =>
            sel.length === 0 ? "All" : `${sel.length} selected`
          }
          displayEmpty
          className="player-select"
          MenuProps={{
            PaperProps: { className: "player-select-menu" },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.name} value={option.name}>
              <Checkbox checked={selected.has(option.name)} size="small" />
              <ListItemText
                primary={`${option.name} (${option.count.toLocaleString()} - ${
                  option.tsPct !== null
                    ? (option.tsPct * 100).toFixed(1) + "%"
                    : "\u2014"
                })`}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
